/**
 * GET    /actions/external-food/random?limit=
 * GET    /actions/external-food/search?q=&cuisine=&diet=&intolerances=&type=&sort=&sortDirection=&page=&limit=
 * GET    /actions/external-food/filters
 * GET    /actions/external-food/details/:id
 * GET    /actions/external-food/favorites/details/:id
 * GET    /actions/external-food/favorites?q=&cuisine=&diet=&intolerances=&type=&sort=&sortDirection=&page=&limit=
 * GET    /actions/external-food/favorite-ids
 * POST   /actions/external-food/favorites/:id — JSON body: the row snapshot the
 *   upstream stores alongside the id (title is required).
 * DELETE /actions/external-food/favorites/:id
 */
import { externalFoodFailBody } from "../utils/external-food-errors";

const UPSTREAM = "spoonacular/recipes";

const DEFAULT_LIMIT = 20;
const MAX_LIMIT = 100;
const RESOURCE_DEFAULT_LIMIT = 25;

const SEARCH_FILTER_KEYS = [
  "cuisine",
  "diet",
  "intolerances",
  "type",
  "sort",
  "sortDirection",
  "maxReadyTime",
  "maxCalories",
  "minProtein",
  "includeIngredients",
  "excludeIngredients",
];

function asJson(res) {
  res.headers = res.headers || {};
  res.headers["Content-Type"] = "application/json; charset=utf-8";
}

function parsePage(value) {
  const page = Number.parseInt(String(value ?? ""), 10);
  return Number.isFinite(page) && page > 0 ? page : 1;
}

function parseLimit(value) {
  const limit = Number.parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(limit) || limit < 1) return DEFAULT_LIMIT;
  if (limit === RESOURCE_DEFAULT_LIMIT) return DEFAULT_LIMIT;
  return Math.min(MAX_LIMIT, limit);
}

function parseJsonBody(body) {
  if (typeof body === "string") {
    try {
      return JSON.parse(body);
    } catch {
      return null;
    }
  }
  return body && typeof body === "object" ? body : {};
}

function pickSearchQuery(query) {
  const out = {
    q: typeof query.q === "string" ? query.q.replace(/\+/g, " ") : query.q,
    page: parsePage(query.page),
    limit: parseLimit(query.limit),
  };
  for (const key of SEARCH_FILTER_KEYS) {
    const value = query[key];
    if (value == null || value === "") continue;
    out[key] = typeof value === "string" ? value.replace(/\+/g, " ") : value;
  }
  return out;
}

async function forward(res, response) {
  const text = await response.text();
  res.status = response.status;
  res.body = text || "{}";
}

function fail(res, error, fallback) {
  const { status, body } = externalFoodFailBody(error, fallback);
  res.status = status;
  res.body = JSON.stringify(body);
}

const ExternalFood = (app) => {
  app
    .get("/external-food/random", async ({ req, res, api }) => {
      asJson(res);
      try {
        const response = await api(`${UPSTREAM}/random`, {
          query: {
            page: parsePage(req.query.page),
            limit: parseLimit(req.query.limit),
          },
        });
        await forward(res, response);
      } catch (e) {
        fail(res, e, "Failed to load external foods");
      }
    })
    .get("/external-food/search", async ({ req, res, api }) => {
      asJson(res);
      try {
        const response = await api(`${UPSTREAM}/search`, {
          query: pickSearchQuery(req.query),
        });
        await forward(res, response);
      } catch (e) {
        fail(res, e, "Failed to search external foods");
      }
    })
    .get("/external-food/filters", async ({ res, api }) => {
      asJson(res);
      try {
        const response = await api(`${UPSTREAM}/filters`);
        await forward(res, response);
      } catch (e) {
        fail(res, e, "Failed to load filters");
      }
    })
    .get("/external-food/details/:id", async ({ req, res, api }) => {
      asJson(res);
      const id = String(req.params.id ?? "").trim();

      if (!id) {
        res.status = 400;
        res.body = JSON.stringify({ error: "Food id is required" });
        return;
      }

      try {
        const response = await api(
          `${UPSTREAM}/details/${encodeURIComponent(id)}`
        );
        await forward(res, response);
      } catch (e) {
        fail(res, e, "Failed to load food details");
      }
    })
    .get("/external-food/favorites/details/:id", async ({ req, res, api }) => {
      asJson(res);
      const id = String(req.params.id ?? "").trim();

      if (!id) {
        res.status = 400;
        res.body = JSON.stringify({ error: "Food id is required" });
        return;
      }

      try {
        const response = await api(
          `${UPSTREAM}/favorite/details/${encodeURIComponent(id)}`
        );
        await forward(res, response);
      } catch (e) {
        fail(res, e, "Failed to load favorite food details");
      }
    })
    .get("/external-food/favorites", async ({ req, res, api }) => {
      asJson(res);
      try {
        const response = await api(`${UPSTREAM}/favorite`, {
          query: pickSearchQuery(req.query),
        });
        await forward(res, response);
      } catch (e) {
        fail(res, e, "Failed to load favorites");
      }
    })
    .get("/external-food/favorite-ids", async ({ res, api }) => {
      asJson(res);
      try {
        const response = await api(`${UPSTREAM}/favorite-ids`);
        await forward(res, response);
      } catch (e) {
        fail(res, e, "Failed to load favorites");
      }
    })
    .post("/external-food/favorites/:id", async ({ req, res, api }) => {
      asJson(res);
      const id = String(req.params.id ?? "").trim();
      const body = parseJsonBody(req.body);

      if (!id) {
        res.status = 400;
        res.body = JSON.stringify({ error: "Food id is required" });
        return;
      }
      if (!body) {
        res.status = 400;
        res.body = JSON.stringify({ error: "Invalid JSON body" });
        return;
      }
      const title =
        body.title ??
        body.name ??
        body.recipe?.name ??
        body.recipe?.title ??
        "";
      if (!String(title).trim()) {
        res.status = 400;
        res.body = JSON.stringify({ error: "title is required" });
        return;
      }

      const payload = {
        ...body,
        title: body.title || title,
      };

      try {
        const response = await api(
          `${UPSTREAM}/favorite/${encodeURIComponent(id)}`,
          {
            method: "POST",
            body: JSON.stringify(payload),
            headers: { "Content-Type": "application/json" },
          }
        );
        await forward(res, response);
      } catch (e) {
        fail(res, e, "Failed to save favorite");
      }
    })
    .delete("/external-food/favorites/:id", async ({ req, res, api }) => {
      asJson(res);
      const id = String(req.params.id ?? "").trim();

      if (!id) {
        res.status = 400;
        res.body = JSON.stringify({ error: "Food id is required" });
        return;
      }

      try {
        const response = await api(
          `${UPSTREAM}/favorite/${encodeURIComponent(id)}`,
          { method: "DELETE" }
        );
        await forward(res, response);
      } catch (e) {
        fail(res, e, "Failed to remove favorite");
      }
    });
};

export default ExternalFood;
